import React, { useCallback,useState } from "react";
import { MoreHorizontal } from "lucide-react";

import { useOverflowDetection } from "./hooks/useOverflowDetection";
import styles from "./TabSwitcher.module.css";

export const TabSwitcher = ({
  options = [],
  value,
  onChange,
  withOverflow = false,
  fullWidth = false,
  size = "default",
  marginTop,
  marginBottom,
  overflowCacheKey,
}) => {
  const { containerRef, tabsRef, visibleCount, hasOverflow } =
    useOverflowDetection(options.length, withOverflow, overflowCacheKey);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const visibleOptions =
    withOverflow && hasOverflow ? options.slice(0, visibleCount) : options;
  const hiddenOptions =
    withOverflow && hasOverflow ? options.slice(visibleCount) : [];

  const handleSelect = useCallback(
    (optionValue) => {
      onChange?.(optionValue);
      setIsDropdownOpen(false);
    },
    [onChange]
  );

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const sizeClass = size === "small" ? styles.small : "";

  const renderTab = (option) => (
    <button
      key={option.value}
      type="button"
      className={`${styles.tab} ${sizeClass} ${value === option.value ? styles.active : ""}`}
      onClick={() => handleSelect(option.value)}
    >
      {option.icon && <span className={styles.tabIcon}>{option.icon}</span>}
      <span>
        {option.label}
        {option.count != null && ` (${option.count})`}
      </span>
    </button>
  );

  const marginStyle =
    marginTop != null || marginBottom != null
      ? { marginTop: marginTop ?? undefined, marginBottom: marginBottom ?? undefined }
      : undefined;

  return (
    <div
      ref={withOverflow ? containerRef : undefined}
      className={`${styles.switcher} ${fullWidth ? styles.fullWidth : ""} ${
        withOverflow ? styles.withOverflow : ""
      } ${sizeClass}`}
      style={marginStyle}
    >
      <div className={styles.tabsContainer}>
        <div ref={withOverflow ? tabsRef : undefined} className={styles.tabs}>
          {visibleOptions.map(renderTab)}
        </div>
        {hasOverflow && hiddenOptions.length > 0 && (
          <div className={styles.overflowWrapper}>
            <button
              type="button"
              className={styles.moreButton}
              onClick={toggleDropdown}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
              data-testid="tab-overflow-button"
            >
              <MoreHorizontal size={20} strokeWidth={2} />
            </button>
            {isDropdownOpen && (
              <>
                <div
                  className={styles.dropdownBackdrop}
                  onClick={() => setIsDropdownOpen(false)}
                  aria-hidden="true"
                />
                <div className={styles.dropdownMenu}>
                  {hiddenOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.dropdownItem} ${
                        value === option.value ? styles.dropdownItemActive : ""
                      }`}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.icon && (
                        <span className={styles.tabIcon}>{option.icon}</span>
                      )}
                      <span>
                        {option.label}
                        {option.count != null && ` (${option.count})`}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
